import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { getChartData } from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import StandardLoader from '../StandardLoader';
import { INDICATOR_DEFAULTS } from '../../utils/indicatorDefaults';

// Data sanitization helper
function sanitizeChartData(data) {
  if (!data || !Array.isArray(data)) {
    console.warn('AdvancedPriceChartWidget: Invalid data format', data);
    return [];
  }

  const sanitized = data.map((item, index) => {
    // Ensure all required fields exist with fallback values
    const sanitized = {
      date: item?.date || `unknown-${index}`,
      close: typeof item?.close === 'number' && !isNaN(item.close) && isFinite(item.close) ? item.close : null,
      open: typeof item?.open === 'number' && !isNaN(item.open) && isFinite(item.open) ? item.open : null,
      high: typeof item?.high === 'number' && !isNaN(item.high) && isFinite(item.high) ? item.high : null,
      low: typeof item?.low === 'number' && !isNaN(item.low) && isFinite(item.low) ? item.low : null,
      volume: typeof item?.volume === 'number' && !isNaN(item.volume) && isFinite(item.volume) ? item.volume : 0,
    };

    // Add indicator data if present - be extra careful with MACD values
    if (item?.macd !== undefined) {
      sanitized.macd = typeof item.macd === 'number' && !isNaN(item.macd) && isFinite(item.macd) ? item.macd : null;
      sanitized.macd_signal = typeof item?.macd_signal === 'number' && !isNaN(item.macd_signal) && isFinite(item.macd_signal) ? item.macd_signal : null;
      sanitized.macd_histogram = typeof item?.macd_histogram === 'number' && !isNaN(item.macd_histogram) && isFinite(item.macd_histogram) ? item.macd_histogram : null;
    }

    if (item?.rsi !== undefined) {
      sanitized.rsi = typeof item.rsi === 'number' && !isNaN(item.rsi) && isFinite(item.rsi) ? item.rsi : null;
    }

    if (item?.bb_upper !== undefined) {
      sanitized.bb_upper = typeof item.bb_upper === 'number' && !isNaN(item.bb_upper) && isFinite(item.bb_upper) ? item.bb_upper : null;
      sanitized.bb_middle = typeof item?.bb_middle === 'number' && !isNaN(item.bb_middle) && isFinite(item.bb_middle) ? item.bb_middle : null;
      sanitized.bb_lower = typeof item?.bb_lower === 'number' && !isNaN(item.bb_lower) && isFinite(item.bb_lower) ? item.bb_lower : null;
    }

    // New indicators
    if (item?.volume_ma !== undefined) {
      sanitized.volume_ma = typeof item.volume_ma === 'number' && !isNaN(item.volume_ma) && isFinite(item.volume_ma) ? item.volume_ma : null;
    }

    if (item?.sma_50 !== undefined) {
      sanitized.sma_50 = typeof item.sma_50 === 'number' && !isNaN(item.sma_50) && isFinite(item.sma_50) ? item.sma_50 : null;
    }

    if (item?.sma_200 !== undefined) {
      sanitized.sma_200 = typeof item.sma_200 === 'number' && !isNaN(item.sma_200) && isFinite(item.sma_200) ? item.sma_200 : null;
    }

    if (item?.sma_720 !== undefined) {
      sanitized.sma_720 = typeof item.sma_720 === 'number' && !isNaN(item.sma_720) && isFinite(item.sma_720) ? item.sma_720 : null;
    }

    if (item?.ema !== undefined) {
      sanitized.ema = typeof item.ema === 'number' && !isNaN(item.ema) && isFinite(item.ema) ? item.ema : null;
    }

    if (item?.vwap !== undefined) {
      sanitized.vwap = typeof item.vwap === 'number' && !isNaN(item.vwap) && isFinite(item.vwap) ? item.vwap : null;
    }

    if (item?.ac !== undefined) {
      sanitized.ac = typeof item.ac === 'number' && !isNaN(item.ac) && isFinite(item.ac) ? item.ac : null;
    }

    return sanitized;
  }).filter(item => item.close !== null); // Remove items without valid close price

  console.log(`[sanitizeChartData] Sanitized ${sanitized.length} data points from ${data.length} original`);

  return sanitized;
}

/**
 * Compute indicator values for the live data point using historical data.
 * Only computes indicators that exist in the previous data point.
 */
function computeLiveIndicators(data) {
  if (data.length < 2) return {};

  const liveIdx = data.length - 1;
  const prevPoint = data[liveIdx - 1];
  const closes = data.map(d => d.close).filter(c => c != null);
  if (closes.length < 2) return {};

  const result = {};

  // Helper: compute SMA of last N values from an array
  const sma = (arr, n) => {
    const slice = arr.slice(-n);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  };

  // EMA: incremental from previous
  if (prevPoint.ema != null) {
    const period = 30; // default EMA period
    const k = 2 / (period + 1);
    result.ema = closes[closes.length - 1] * k + prevPoint.ema * (1 - k);
  }

  // SMA overlays
  if (prevPoint.sma_50 != null) result.sma_50 = sma(closes, 50);
  if (prevPoint.sma_200 != null) result.sma_200 = sma(closes, 200);
  if (prevPoint.sma_720 != null) result.sma_720 = sma(closes, 720);

  // Bollinger Bands: SMA(20) ± 2*stddev(20)
  if (prevPoint.bb_upper != null) {
    const n = Math.min(20, closes.length);
    const slice = closes.slice(-n);
    const mean = slice.reduce((a, b) => a + b, 0) / slice.length;
    const variance = slice.reduce((s, v) => s + (v - mean) ** 2, 0) / slice.length;
    const stddev = Math.sqrt(variance);
    result.bb_middle = mean;
    result.bb_upper = mean + 2 * stddev;
    result.bb_lower = mean - 2 * stddev;
  }

  // MACD: compute fast/slow EMAs from scratch
  if (prevPoint.macd != null && closes.length >= 26) {
    const kFast = 2 / 13, kSlow = 2 / 27, kSignal = 2 / 10;
    let emaFast = closes[0], emaSlow = closes[0];
    const macdValues = [];
    for (let i = 1; i < closes.length; i++) {
      emaFast = closes[i] * kFast + emaFast * (1 - kFast);
      emaSlow = closes[i] * kSlow + emaSlow * (1 - kSlow);
      macdValues.push(emaFast - emaSlow);
    }
    result.macd = macdValues[macdValues.length - 1];
    // Signal line: EMA(9) of MACD values
    let signal = macdValues[0];
    for (let i = 1; i < macdValues.length; i++) {
      signal = macdValues[i] * kSignal + signal * (1 - kSignal);
    }
    result.macd_signal = signal;
    result.macd_histogram = result.macd - result.macd_signal;
  }

  // RSI: avg gain/loss over last 14 price changes
  if (prevPoint.rsi != null && closes.length > 14) {
    const period = 14;
    const recent = closes.slice(-(period + 1));
    let avgGain = 0, avgLoss = 0;
    for (let i = 1; i < recent.length; i++) {
      const change = recent[i] - recent[i - 1];
      if (change > 0) avgGain += change; else avgLoss += Math.abs(change);
    }
    avgGain /= period;
    avgLoss /= period;
    result.rsi = avgLoss === 0 ? 100 : 100 - (100 / (1 + avgGain / avgLoss));
  }

  // AC: AO = SMA5(median) - SMA34(median), AC = AO - SMA5(AO)
  if (prevPoint.ac != null) {
    // Use close as proxy for median since live point lacks high/low
    const medians = data.map(d => {
      if (d.high != null && d.low != null) return (d.high + d.low) / 2;
      return d.close;
    }).filter(v => v != null);
    if (medians.length >= 34) {
      const aoValues = [];
      for (let i = 33; i < medians.length; i++) {
        const sma5 = medians.slice(i - 4, i + 1).reduce((a, b) => a + b, 0) / 5;
        const sma34 = medians.slice(i - 33, i + 1).reduce((a, b) => a + b, 0) / 34;
        aoValues.push(sma5 - sma34);
      }
      if (aoValues.length >= 5) {
        const aoSma5 = aoValues.slice(-5).reduce((a, b) => a + b, 0) / 5;
        result.ac = aoValues[aoValues.length - 1] - aoSma5;
      }
    }
  }

  return result;
}

function AdvancedPriceChartWidget({ ticker, dateRange, indicators = [], indicatorConfig = null, isLinked, crosshairDate, onCrosshairMove, showBollinger, setShowBollinger, crossChartData, onCrosshairData, livePrice, liveTimestamp }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);

  // Refs for live data to avoid adding to loadChartData's dependency array
  const livePriceRef = useRef(livePrice);
  const liveTimestampRef = useRef(liveTimestamp);
  useEffect(() => { livePriceRef.current = livePrice; }, [livePrice]);
  useEffect(() => { liveTimestampRef.current = liveTimestamp; }, [liveTimestamp]);

  // Support both legacy indicators array and new indicatorConfig object
  const safeIndicators = Array.isArray(indicators) ? indicators : [];
  const hasConfig = indicatorConfig && typeof indicatorConfig === 'object';

  const loadChartData = async () => {
    setLoading(true);
    setError(null);
    setWarnings([]);
    try {
      let response;

      if (hasConfig) {
        // New format: Use indicator config object
        const configParam = JSON.stringify(indicatorConfig);
        console.log(`[AdvancedPriceChartWidget] Loading data for ${ticker} with config:`, indicatorConfig);
        response = await getChartData(ticker, dateRange, null, configParam);
      } else {
        // Legacy format: Use comma-separated indicators
        const indicatorsParam = safeIndicators.join(',');
        console.log(`[AdvancedPriceChartWidget] Loading data for ${ticker} with indicators: ${indicatorsParam}`);
        response = await getChartData(ticker, dateRange, indicatorsParam);
      }

      console.log('[AdvancedPriceChartWidget] Response:', response);

      // Check for backend warnings about indicator data
      if (response?.data?.warnings && response.data.warnings.length > 0) {
        setWarnings(response.data.warnings);
        console.warn('[AdvancedPriceChartWidget] Indicator warnings:', response.data.warnings);
      }

      // Validate and sanitize the response data
      const rawData = response?.data?.data;
      console.log(`[AdvancedPriceChartWidget] Raw data points: ${rawData?.length || 0}`);

      const sanitizedData = sanitizeChartData(rawData);
      console.log(`[AdvancedPriceChartWidget] Sanitized data points: ${sanitizedData.length}`);

      if (sanitizedData.length === 0) {
        setError('No valid data available for this ticker');
      } else {
        // Append or update live data point if available
        const lp = livePriceRef.current;
        const lt = liveTimestampRef.current;
        if (lp && !isNaN(lp) && sanitizedData.length > 0) {
          const now = new Date();
          const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
          const lastDate = sanitizedData[sanitizedData.length - 1]?.date;
          if (lastDate < todayStr) {
            // Historical data is behind — append a new live point
            sanitizedData.push({
              date: todayStr,
              close: lp,
              open: null, high: null, low: null,
              volume: null,
              isLivePoint: true,
              liveTimestamp: lt,
            });
            // Compute indicator values for the appended live point
            const liveIndicators = computeLiveIndicators(sanitizedData);
            Object.assign(sanitizedData[sanitizedData.length - 1], liveIndicators);
          } else if (lastDate === todayStr) {
            // Today's bar exists but may be stale EOD — update with live price
            sanitizedData[sanitizedData.length - 1] = {
              ...sanitizedData[sanitizedData.length - 1],
              close: lp,
              isLivePoint: true,
              liveTimestamp: lt,
            };
            // Recompute indicators with the live close for accuracy
            const liveIndicators = computeLiveIndicators(sanitizedData);
            Object.assign(sanitizedData[sanitizedData.length - 1], liveIndicators);
          }
        }
        setChartData(sanitizedData);
        // Set showBollinger if bollinger is in indicators
        setShowBollinger(safeIndicators.includes('bollinger'));

        // Log sample of MACD data if present
        if (safeIndicators.includes('macd') && sanitizedData.length > 0) {
          const lastPoint = sanitizedData[sanitizedData.length - 1];
          console.log('[AdvancedPriceChartWidget] Last MACD values:', {
            macd: lastPoint.macd,
            signal: lastPoint.macd_signal,
            histogram: lastPoint.macd_histogram
          });
        }
      }
    } catch (err) {
      console.error('Error loading chart data:', err);
      setError(`Failed to load chart data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Load chart data when ticker, range, indicators, or config change
  useEffect(() => {
    if (ticker) {
      loadChartData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, dateRange, safeIndicators.join(','), JSON.stringify(indicatorConfig)]);

  // Auto-enable Bollinger Bands display when data is available
  useEffect(() => {
    if (chartData && chartData.length > 0 && safeIndicators.includes('bollinger')) {
      setShowBollinger(true);
    }
  }, [chartData, safeIndicators]);

  // Memoize event handlers to prevent recreation on every render
  const handleMouseMove = useCallback((data) => {
    if (!isLinked || !data || !data.activeLabel) return;
    if (onCrosshairMove) onCrosshairMove(data.activeLabel);
    if (onCrosshairData && data.activePayload?.[0]?.payload) {
      onCrosshairData(data.activePayload[0].payload);
    }
  }, [isLinked, onCrosshairMove, onCrosshairData]);

  const handleMouseLeave = useCallback(() => {
    if (isLinked) {
      if (onCrosshairMove) onCrosshairMove(null);
      if (onCrosshairData) onCrosshairData(null);
    }
  }, [isLinked, onCrosshairMove, onCrosshairData]);

  // When the OTHER chart moves the shared crosshair, look up our data at that date
  // and report it via onCrosshairData so the other chart's tooltip can display it.
  useEffect(() => {
    if (!isLinked || !crosshairDate || !onCrosshairData || chartData.length === 0) return;
    const match = chartData.find(d => d.date === crosshairDate);
    if (match) {
      onCrosshairData(match);
    }
  }, [isLinked, crosshairDate, chartData, onCrosshairData]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <StandardLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-background p-4">
        <p className="text-red-500 text-center mb-2">{error}</p>
        <button
          onClick={loadChartData}
          className="text-xs text-blue-400 hover:text-blue-300 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <p className="text-gray-400">No data available</p>
      </div>
    );
  }

  // Detect which indicators are present in the data
  // Support both legacy array format and new config object format
  const activeIndicators = hasConfig ? Object.keys(indicatorConfig) : safeIndicators;

  const hasMACD = (activeIndicators.includes('macd') || safeIndicators.includes('macd')) && chartData.some(d => d.macd !== null);
  const hasRSI = (activeIndicators.includes('rsi') || safeIndicators.includes('rsi')) && chartData.some(d => d.rsi !== null);
  const hasBollinger = (activeIndicators.includes('bollinger') || safeIndicators.includes('bollinger')) && chartData.some(d => d.bb_upper !== null);
  const hasVolume = activeIndicators.includes('volume') && chartData.some(d => d.volume_ma !== null);
  const hasSMA50 = activeIndicators.includes('sma_50') && chartData.some(d => d.sma_50 !== null);
  const hasSMA200 = activeIndicators.includes('sma_200') && chartData.some(d => d.sma_200 !== null);
  const hasSMA720 = activeIndicators.includes('sma_720') && chartData.some(d => d.sma_720 !== null);
  const hasEMA = activeIndicators.includes('ema') && chartData.some(d => d.ema !== null);
  const hasVWAP = activeIndicators.includes('vwap') && chartData.some(d => d.vwap !== null);
  const hasAC = activeIndicators.includes('ac') && chartData.some(d => d.ac !== null);

  // Get colors from config or use defaults
  const getIndicatorColor = (indicatorName, colorKey) => {
    if (hasConfig && indicatorConfig[indicatorName]?.colors?.[colorKey]) {
      return indicatorConfig[indicatorName].colors[colorKey];
    }
    if (hasConfig && indicatorConfig[indicatorName]?.color) {
      return indicatorConfig[indicatorName].color;
    }
    return INDICATOR_DEFAULTS[indicatorName]?.colors?.[colorKey] || INDICATOR_DEFAULTS[indicatorName]?.color || '#6b7280';
  };

  // Debug logging
  console.log('[AdvancedPriceChartWidget] Indicator checks:', {
    activeIndicators,
    hasMACD,
    hasRSI,
    hasBollinger,
    hasVolume,
    hasSMA50,
    hasSMA200,
    hasEMA,
    hasVWAP,
    hasAC,
    chartDataLength: chartData.length
  });

  // Calculate min and max for price chart with safer operations
  const validPrices = chartData.map(d => d.close).filter(p => p !== null && !isNaN(p));
  let priceMin = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  let priceMax = validPrices.length > 0 ? Math.max(...validPrices) : 100;

  if (hasBollinger && showBollinger) {
    const validBBLower = chartData
      .map(d => d.bb_lower)
      .filter(v => v !== null && !isNaN(v));
    const validBBUpper = chartData
      .map(d => d.bb_upper)
      .filter(v => v !== null && !isNaN(v));

    if (validBBLower.length > 0) {
      priceMin = Math.min(priceMin, Math.min(...validBBLower));
    }
    if (validBBUpper.length > 0) {
      priceMax = Math.max(priceMax, Math.max(...validBBUpper));
    }
  }

  // Ensure we have valid price range
  if (priceMin === priceMax) {
    priceMax = priceMin + 1;
  }

  // Add padding to price range
  const pricePadding = (priceMax - priceMin) * 0.1;
  priceMin = Math.max(0, priceMin - pricePadding);
  priceMax = priceMax + pricePadding;

  // Custom tooltip with better error handling
  // Uses payload[0]?.payload to access the FULL data point from chartData,
  // so all indicator values show regardless of which sub-chart is hovered.
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !Array.isArray(payload) || payload.length === 0) {
      return null;
    }

    try {
      // Parse YYYY-MM-DD directly to avoid timezone offset issues with new Date()
      let formattedDate = label || 'Unknown date';
      if (label && label.includes('-')) {
        const [y, m, d] = label.split('-');
        formattedDate = `${parseInt(m)}/${parseInt(d)}/${y}`;
      }

      // Full data point — contains ALL fields (close, macd, rsi, ema, etc.)
      const d = payload[0]?.payload;
      if (!d) return null;

      // When linked, look up cross-chart data (premium) at the same date
      const cc = isLinked && crossChartData ? crossChartData : null;

      return (
        <div className="bg-gray-900 border border-gray-700 p-2 rounded text-xs shadow-lg">
          <p className="text-gray-400 mb-1">{formattedDate}</p>
          {d.isLivePoint && (
            <p className="text-yellow-400 text-[10px] font-semibold mb-1">
              {d.liveTimestamp
                ? `As of ${new Date(d.liveTimestamp).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}`
                : 'LIVE'}
            </p>
          )}

          {/* Price — always show */}
          {d.close != null && (
            <p className="text-green-400">Price: ${d.close.toFixed(2)}</p>
          )}

          {/* Bollinger Bands */}
          {showBollinger && hasBollinger && d.bb_upper != null && (
            <p className="text-blue-400">BB: ${d.bb_lower?.toFixed(2)}-${d.bb_upper.toFixed(2)}</p>
          )}

          {/* SMA / EMA / VWAP overlays */}
          {hasSMA50 && d.sma_50 != null && (
            <p style={{ color: getIndicatorColor('sma_50', 'color') }}>SMA50: ${d.sma_50.toFixed(2)}</p>
          )}
          {hasSMA200 && d.sma_200 != null && (
            <p style={{ color: getIndicatorColor('sma_200', 'color') }}>SMA200: ${d.sma_200.toFixed(2)}</p>
          )}
          {hasSMA720 && d.sma_720 != null && (
            <p style={{ color: getIndicatorColor('sma_720', 'color') }}>SMA720: ${d.sma_720.toFixed(2)}</p>
          )}
          {hasEMA && d.ema != null && (
            <p style={{ color: getIndicatorColor('ema', 'color') }}>EMA: ${d.ema.toFixed(2)}</p>
          )}
          {hasVWAP && d.vwap != null && (
            <p style={{ color: getIndicatorColor('vwap', 'color') }}>VWAP: ${d.vwap.toFixed(2)}</p>
          )}

          {/* MACD */}
          {hasMACD && d.macd != null && (
            <>
              <p className="text-cyan-400">MACD: {d.macd.toFixed(3)}</p>
              {d.macd_signal != null && <p className="text-orange-400">Signal: {d.macd_signal.toFixed(3)}</p>}
              {d.macd_histogram != null && <p className="text-gray-400">Hist: {d.macd_histogram.toFixed(3)}</p>}
            </>
          )}

          {/* RSI */}
          {hasRSI && d.rsi != null && (
            <p className="text-purple-400">RSI: {d.rsi.toFixed(1)}</p>
          )}

          {/* Volume */}
          {d.volume != null && d.volume > 0 && (
            <p className="text-gray-500">Vol: {d.volume.toLocaleString()}</p>
          )}

          {/* AC */}
          {hasAC && d.ac != null && (
            <p style={{ color: d.ac >= 0 ? getIndicatorColor('ac', 'bullish') : getIndicatorColor('ac', 'bearish') }}>
              AC: {d.ac.toFixed(3)}
            </p>
          )}

          {/* Cross-chart data: premium info */}
          {cc && (
            <div className="border-t border-gray-700 mt-1.5 pt-1.5">
              {cc.close != null && (
                <p className="text-red-400">Premium: ${cc.close.toFixed(2)}</p>
              )}
              {cc.high != null && cc.low != null && (
                <p className="text-gray-300">H: ${cc.high.toFixed(2)} / L: ${cc.low.toFixed(2)}</p>
              )}
              {cc.volume != null && cc.volume > 0 && (
                <p className="text-gray-500">Opt Vol: {cc.volume.toLocaleString()}</p>
              )}
            </div>
          )}
        </div>
      );
    } catch (err) {
      console.error('Tooltip render error:', err);
      return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-950">
      {/* Warning Banner */}
      {warnings.length > 0 && (
        <div className="bg-yellow-900/20 border-b border-yellow-700/50 px-3 py-2">
          <div className="flex items-start gap-2">
            <span className="text-yellow-400 text-xs">⚠️</span>
            <div className="flex-1">
              {warnings.map((warning, idx) => (
                <p key={idx} className="text-yellow-300 text-xs leading-tight">
                  {warning}
                </p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Multi-Panel Chart Area */}
      <div className="flex-1 flex flex-col gap-1 p-2">
        {chartData.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-400 text-sm">No chart data available</p>
          </div>
        ) : (
          <>
            {/* Price Chart Panel (always visible) */}
            <div className="flex-[300] relative">
              {hasBollinger && showBollinger && (
                <div className="absolute top-2 left-2 z-10">
                  <span className="text-[11px] text-gray-500 font-medium">
                    Bollinger Bands (20)
                  </span>
                </div>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  margin={{ top: 5, right: 5, left: 5, bottom: hasMACD || hasRSI ? 0 : 20 }}
                >
                  <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke="#4b5563"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    hide={hasMACD || hasRSI}
                    tickFormatter={(value) => {
                      try {
                        if (value && value.includes('-')) {
                          const [, m, d] = value.split('-');
                          return `${parseInt(m)}/${parseInt(d)}`;
                        }
                      } catch (e) {}
                      return '';
                    }}
                  />
                  <YAxis
                    stroke="#4b5563"
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                    domain={[priceMin, priceMax]}
                    tickFormatter={(value) => `$${value?.toFixed(0) || '0'}`}
                  />
                  <Tooltip content={<CustomTooltip />} />

                  {/* Bollinger Bands - brighter and more visible */}
                  {hasBollinger && showBollinger && (
                    <>
                      <Line
                        type="monotone"
                        dataKey="bb_upper"
                        stroke="#60a5fa"
                        strokeWidth={1}
                        dot={false}
                        strokeDasharray="3 3"
                        opacity={0.7}
                        connectNulls={true}
                      />
                      <Line
                        type="monotone"
                        dataKey="bb_lower"
                        stroke="#60a5fa"
                        strokeWidth={1}
                        dot={false}
                        strokeDasharray="3 3"
                        opacity={0.7}
                        connectNulls={true}
                      />
                      <Line
                        type="monotone"
                        dataKey="bb_middle"
                        stroke="#60a5fa"
                        strokeWidth={0.8}
                        dot={false}
                        strokeDasharray="2 2"
                        opacity={0.5}
                        connectNulls={true}
                      />
                    </>
                  )}

                  {/* Main Price Line */}
                  <Line
                    type="monotone"
                    dataKey="close"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload, index } = props;
                      if (!payload?.isLivePoint) return <circle key={`dot-${index}`} r={0} />;
                      return (
                        <circle
                          key={`dot-${index}`}
                          cx={cx} cy={cy} r={5}
                          fill="#10b981"
                          stroke="#ffffff"
                          strokeWidth={2}
                        />
                      );
                    }}
                    activeDot={{ r: 4 }}
                    connectNulls={true}
                  />

                  {/* SMA 50 Overlay */}
                  {hasSMA50 && (
                    <Line
                      type="monotone"
                      dataKey="sma_50"
                      stroke={getIndicatorColor('sma_50', 'color')}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                      name="SMA 50"
                    />
                  )}

                  {/* SMA 200 Overlay */}
                  {hasSMA200 && (
                    <Line
                      type="monotone"
                      dataKey="sma_200"
                      stroke={getIndicatorColor('sma_200', 'color')}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                      name="SMA 200"
                    />
                  )}

                  {/* SMA 720 Overlay */}
                  {hasSMA720 && (
                    <Line
                      type="monotone"
                      dataKey="sma_720"
                      stroke={getIndicatorColor('sma_720', 'color')}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                      name="SMA 720"
                    />
                  )}

                  {/* EMA Overlay */}
                  {hasEMA && (
                    <Line
                      type="monotone"
                      dataKey="ema"
                      stroke={getIndicatorColor('ema', 'color')}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                      name="EMA"
                    />
                  )}

                  {/* VWAP Overlay */}
                  {hasVWAP && (
                    <Line
                      type="monotone"
                      dataKey="vwap"
                      stroke={getIndicatorColor('vwap', 'color')}
                      strokeWidth={1.5}
                      strokeDasharray="5 5"
                      dot={false}
                      connectNulls={true}
                      name="VWAP"
                    />
                  )}

                  {/* Synchronized crosshair */}
                  {isLinked && crosshairDate && (
                    <ReferenceLine
                      x={crosshairDate}
                      stroke="rgba(255, 255, 255, 0.3)"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* MACD Chart Panel (conditional) */}
            {hasMACD && (
              <div className="flex-[150] border-t border-gray-800 relative">
                <div className="absolute top-2 left-2 z-10">
                  <span className="text-[11px] text-gray-500 font-medium">
                    MACD (12, 26, 9)
                  </span>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartData}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    margin={{ top: 5, right: 5, left: 5, bottom: hasRSI ? 0 : 20 }}
                  >
                    <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      hide={hasRSI}
                      tickFormatter={(value) => {
                        try {
                          if (value && value.includes('-')) {
                            const [, m, d] = value.split('-');
                            return `${parseInt(m)}/${parseInt(d)}`;
                          }
                        } catch (e) {}
                        return '';
                      }}
                    />
                    <YAxis
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Zero line */}
                    <ReferenceLine y={0} stroke="#4b5563" strokeWidth={1} />

                    {/* MACD Histogram as bars */}
                    <Line
                      type="monotone"
                      dataKey="macd_histogram"
                      stroke="#6b7280"
                      strokeWidth={1}
                      dot={false}
                      fill="#6b7280"
                      connectNulls={true}
                    />

                    {/* MACD Line */}
                    <Line
                      type="monotone"
                      dataKey="macd"
                      stroke="#06b6d4"
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                    />

                    {/* Signal Line */}
                    <Line
                      type="monotone"
                      dataKey="macd_signal"
                      stroke="#fb923c"
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                    />

                    {/* Synchronized crosshair */}
                    {isLinked && crosshairDate && (
                      <ReferenceLine
                        x={crosshairDate}
                        stroke="rgba(255, 255, 255, 0.3)"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* RSI Chart Panel (conditional) */}
            {hasRSI && (
              <div className="flex-[150] border-t border-gray-800 relative">
                <div className="absolute top-2 left-2 z-10">
                  <span className="text-[11px] text-gray-500 font-medium">
                    RSI (14)
                  </span>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      tickFormatter={(value) => {
                        try {
                          if (value && value.includes('-')) {
                            const [, m, d] = value.split('-');
                            return `${parseInt(m)}/${parseInt(d)}`;
                          }
                        } catch (e) {}
                        return '';
                      }}
                    />
                    <YAxis
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      domain={[0, 100]}
                      ticks={[0, 30, 50, 70, 100]}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Overbought/Oversold zones */}
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={1} opacity={0.3} />
                    <ReferenceLine y={30} stroke="#10b981" strokeDasharray="3 3" strokeWidth={1} opacity={0.3} />
                    <ReferenceLine y={50} stroke="#4b5563" strokeDasharray="1 3" strokeWidth={0.5} opacity={0.2} />

                    {/* RSI Line */}
                    <Line
                      type="monotone"
                      dataKey="rsi"
                      stroke="#a855f7"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls={true}
                    />

                    {/* Synchronized crosshair */}
                    {isLinked && crosshairDate && (
                      <ReferenceLine
                        x={crosshairDate}
                        stroke="rgba(255, 255, 255, 0.3)"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Volume Panel (conditional) */}
            {hasVolume && (
              <div className="flex-[120] border-t border-gray-800">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartData}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    margin={{ top: 5, right: 5, left: 5, bottom: hasAC ? 0 : 20 }}
                  >
                    <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      hide={hasAC}
                      tickFormatter={(value) => {
                        try {
                          if (value && value.includes('-')) {
                            const [, m, d] = value.split('-');
                            return `${parseInt(m)}/${parseInt(d)}`;
                          }
                        } catch (e) {}
                        return '';
                      }}
                    />
                    <YAxis
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Volume Bars */}
                    <Bar
                      dataKey="volume"
                      fill={getIndicatorColor('volume', 'bars')}
                      opacity={0.6}
                    />

                    {/* Volume MA Line */}
                    <Line
                      type="monotone"
                      dataKey="volume_ma"
                      stroke={getIndicatorColor('volume', 'ma')}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls={true}
                    />

                    {/* Synchronized crosshair */}
                    {isLinked && crosshairDate && (
                      <ReferenceLine
                        x={crosshairDate}
                        stroke="rgba(255, 255, 255, 0.3)"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* AC Panel (conditional) */}
            {hasAC && (
              <div className="flex-[120] border-t border-gray-800 relative">
                <div className="absolute top-2 left-2 z-10">
                  <span className="text-[11px] text-gray-500 font-medium">
                    AC
                  </span>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="1 3" stroke="#1f2937" vertical={false} />
                    <XAxis
                      dataKey="date"
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      tickFormatter={(value) => {
                        try {
                          if (value && value.includes('-')) {
                            const [, m, d] = value.split('-');
                            return `${parseInt(m)}/${parseInt(d)}`;
                          }
                        } catch (e) {}
                        return '';
                      }}
                    />
                    <YAxis
                      stroke="#4b5563"
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip content={<CustomTooltip />} />

                    {/* Zero line */}
                    <ReferenceLine y={0} stroke="#4b5563" strokeWidth={1} />

                    {/* AC Bars - color based on value. Recharts requires a
                        static `fill` on <Bar>; per-bar coloring is done via
                        <Cell> children. The prior function-as-fill silently
                        rendered no bars. */}
                    <Bar dataKey="ac" fill={getIndicatorColor('ac', 'bullish')}>
                      {chartData.map((entry, idx) => (
                        <Cell
                          key={`ac-cell-${idx}`}
                          fill={(entry.ac ?? 0) >= 0
                            ? getIndicatorColor('ac', 'bullish')
                            : getIndicatorColor('ac', 'bearish')}
                        />
                      ))}
                    </Bar>

                    {/* Synchronized crosshair */}
                    {isLinked && crosshairDate && (
                      <ReferenceLine
                        x={crosshairDate}
                        stroke="rgba(255, 255, 255, 0.3)"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>

      {/* Chart Info Footer */}
      <div className="px-2 py-1 bg-gray-900 border-t border-gray-800 flex items-center justify-between text-xs">
        <div className="text-gray-500 flex items-center gap-2 flex-wrap">
          <span>{ticker} Price</span>
          {showBollinger && hasBollinger && <span>• BB</span>}
          {hasMACD && <span>• MACD</span>}
          {hasRSI && <span>• RSI</span>}
          {hasSMA50 && <span>• SMA50</span>}
          {hasSMA200 && <span>• SMA200</span>}
          {hasSMA720 && <span>• SMA720</span>}
          {hasEMA && <span>• EMA</span>}
          {hasVWAP && <span>• VWAP</span>}
          {hasVolume && <span>• Vol</span>}
          {hasAC && <span>• AC</span>}
        </div>
        <div className="text-gray-600">
          {dateRange?.toUpperCase() || 'N/A'}
        </div>
      </div>
    </div>
  );
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when props actually change
export default memo(AdvancedPriceChartWidget, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Return false if props changed (do re-render)
  return (
    prevProps.ticker === nextProps.ticker &&
    prevProps.dateRange === nextProps.dateRange &&
    prevProps.isLinked === nextProps.isLinked &&
    prevProps.showBollinger === nextProps.showBollinger &&
    prevProps.crosshairDate === nextProps.crosshairDate &&
    prevProps.crossChartData === nextProps.crossChartData &&
    prevProps.livePrice === nextProps.livePrice &&
    JSON.stringify(prevProps.indicators) === JSON.stringify(nextProps.indicators) &&
    JSON.stringify(prevProps.indicatorConfig) === JSON.stringify(nextProps.indicatorConfig)
  );
});